import {
  CHECKLIST_CATEGORIES,
  CHECKLIST_LIMITS,
  effectiveChecklistTemplate,
  kysFormLabel,
  validateChecklistTemplate,
  type ChecklistAssignment,
  type ChecklistCategory,
  type ChecklistItemDef,
  type ChecklistTemplate,
  type CurrentUser,
  type TemplateIssue,
} from "@fts/core";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { ConfirmDialog, Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { useFeedback } from "../components/Toast";
import { PageHeader, Panel, Loading, EmptyState, Note } from "../components/ui";
import { useI18n } from "../i18n";
import { useSession, type TenantData } from "../state/session";
import { itemText, templateName } from "./ChecklistParts";

/**
 * Checklist templates (ADR 0016): the five standard templates, the tenant's copies (edit items, order and
 * flags) and which template each vehicle type uses. Editing: fleet managers WITHOUT a site scope and
 * admins (templates are tenant-wide).
 */
export function canEditTemplates(user: CurrentUser | null, tenantId: string | undefined): boolean {
  if (!user || !tenantId) return false;
  if (user.platformAdmin) return true;
  const m = user.memberships.find((x) => x.tenantId === tenantId);
  return !!m && (m.role === "admin" || (m.role === "fleet_manager" && m.siteIds.length === 0));
}

type EditItem = ChecklistItemDef & { id?: string; key: string };
let keySeq = 0;

export function ChecklistTemplatesPage(_props: { data: TenantData }) {
  const { t } = useI18n();
  const { provider, tenant, user } = useSession();
  const feedback = useFeedback();
  const [state, setState] = useState<{ templates: ChecklistTemplate[]; assignments: ChecklistAssignment[] } | null>(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState<ChecklistTemplate | null>(null);
  const [viewing, setViewing] = useState<ChecklistTemplate | null>(null);
  const [deleting, setDeleting] = useState<ChecklistTemplate | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const tenantId = tenant?.id;
  const editable = canEditTemplates(user, tenantId);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [templates, assignments] = await Promise.all([provider.listChecklistTemplates(tenantId), provider.listChecklistAssignments(tenantId)]);
      setState({ templates, assignments });
      setError(false);
    } catch {
      setError(true);
    }
  }, [provider, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const copy = async (tpl: ChecklistTemplate) => {
    if (!tenantId) return;
    setBusy(tpl.id);
    try {
      const id = await provider.copyChecklistTemplate(tenantId, tpl.id, t("templates.copyName", { name: templateName(t, tpl) }).slice(0, CHECKLIST_LIMITS.templateNameMax));
      const templates = await provider.listChecklistTemplates(tenantId);
      setState((s) => (s ? { ...s, templates } : s));
      const created = templates.find((x) => x.id === id);
      feedback.ok(t("toast.templateCopied"));
      if (created) setEditing(created);
    } catch (e) {
      // No form is open for a row action: the error goes to a toast (ADR 0029).
      feedback.fail(e, t("toast.copyFailed"));
    } finally {
      setBusy(null);
    }
  };

  const assign = async (category: ChecklistCategory, templateId: string | null) => {
    if (!tenantId) return;
    try {
      await provider.setChecklistAssignment(tenantId, category, templateId);
      await load();
      feedback.ok(t("toast.assignmentSaved"));
    } catch (e) {
      feedback.fail(e, t("toast.saveFailed"));
    }
  };

  if (error) return <EmptyState icon="alert" title={t("common.loadError")} />;
  if (!state)
    return (
      <Loading />
    );
  const own = state.templates.filter((x) => x.tenantId !== null);
  // KYS library templates (ADR 0033): no tenant and no category; copied or assigned like the standard ones.
  const library = state.templates.filter((x) => x.tenantId === null && x.systemCategory === null);
  const system = state.templates.filter((x) => x.tenantId === null && x.systemCategory !== null).sort((a, b) => CHECKLIST_CATEGORIES.indexOf(a.systemCategory!) - CHECKLIST_CATEGORIES.indexOf(b.systemCategory!));

  return (
    <>
      <PageHeader title={t("templates.title")} subtitle={t("templates.subtitle")} />
      {!editable && <Note>{t("templates.viewOnly")}</Note>}

      <Panel title={t("templates.assignTitle")} meta={t("templates.assignHint")} flush>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t("templates.category")}</th>
                <th scope="col">{t("templates.template")}</th>
              </tr>
            </thead>
            <tbody>
              {CHECKLIST_CATEGORIES.map((cat) => {
                const current = effectiveChecklistTemplate(cat, state.assignments, state.templates);
                const assigned = state.assignments.find((a) => a.category === cat)?.templateId ?? "";
                const selectId = `assign-${cat}`;
                return (
                  <tr key={cat}>
                    <td>
                      <label htmlFor={selectId}>{t(`checklist.category.${cat}`)}</label>
                    </td>
                    <td>
                      {editable ? (
                        <select id={selectId} value={assigned} onChange={(e) => void assign(cat, e.target.value || null)}>
                          <option value="">{t("templates.useSystem")}</option>
                          {own.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.name}
                            </option>
                          ))}
                          {library.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span id={selectId}>{templateName(t, state.templates.find((x) => x.id === current) ?? { name: "—", systemCategory: cat })}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title={t("templates.title")} flush>
        <ul className="cl-template-list">
          {[...system, ...library, ...own].map((tpl) => (
            <li key={tpl.id} className="cl-template">
              <div>
                <div className="strong">{templateName(t, tpl)}</div>
                <div className="muted small">
                  <span className={`badge tone-${tpl.tenantId ? "info" : "neutral"}`}>
                    {tpl.tenantId ? t("templates.own") : tpl.systemCategory ? t("templates.system") : t("kys.template.library")}
                  </span>{" "}
                  {tpl.form && <span className="badge tone-warn">{kysFormLabel(tpl.form)}</span>}{" "}
                  {t("templates.items", { n: tpl.items.length })} · {t("templates.critical", { n: tpl.items.filter((i) => i.critical).length })}
                </div>
              </div>
              <div className="cl-template-actions">
                <button type="button" className="button button-secondary button-sm" onClick={() => setViewing(tpl)}>
                  {t("templates.view")}
                </button>
                {editable && (
                  <button type="button" className="button button-secondary button-sm" disabled={busy === tpl.id} onClick={() => void copy(tpl)}>
                    <Icon name="copy" size={15} />
                    {t("templates.copy")}
                  </button>
                )}
                {editable && tpl.tenantId && (
                  <>
                    <button type="button" className="button button-secondary button-sm" onClick={() => setEditing(tpl)}>
                      <Icon name="edit" size={15} />
                      {t("common.edit")}
                    </button>
                    <button type="button" className="button button-secondary button-sm button-danger-text" onClick={() => setDeleting(tpl)}>
                      <Icon name="trash" size={15} />
                      {t("templates.delete")}
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      {viewing && <TemplateView template={viewing} onClose={() => setViewing(null)} />}
      {editing && (
        <TemplateEditor
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            feedback.ok(t("toast.templateSaved"));
            await load();
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={t("templates.deleteTitle")}
          confirmLabel={t("templates.delete")}
          danger
          successMessage={t("toast.templateDeleted")}
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await provider.deleteChecklistTemplate(deleting.id);
            setDeleting(null);
            await load();
          }}
        >
          <strong>{deleting.name}</strong> — {t("templates.deleteText")}
        </ConfirmDialog>
      )}
    </>
  );
}

function TemplateView({ template, onClose }: { template: ChecklistTemplate; onClose: () => void }) {
  const { t, lang } = useI18n();
  return (
    <Dialog
      title={templateName(t, template)}
      description={t("templateEdit.viewTitle")}
      onClose={onClose}
      wide
      footer={
        <button type="button" className="button button-secondary" onClick={onClose}>
          {t("checklistQr.close")}
        </button>
      }
    >
      <ol className="cl-view-items">
        {template.items.map((i) => (
          <li key={i.id}>
            <span className="mono muted">{i.code}</span> {itemText(lang, i)}{" "}
            {i.critical && <span className="badge tone-crit cl-inline-badge">{t("checklist.critical")}</span>}
            {i.photoRequiredOnFail && <span className="badge tone-info cl-inline-badge">{t("checklist.photoRequired")}</span>}
            {i.critical && i.allowNa && <span className="badge tone-neutral cl-inline-badge">{t("checklist.allowNa")}</span>}
            {i.powerTypes.length > 0 && <span className="badge tone-neutral cl-inline-badge">{i.powerTypes.map((p) => t(`power.${p}`)).join(", ")}</span>}
          </li>
        ))}
      </ol>
    </Dialog>
  );
}

function TemplateEditor({ template, onClose, onSaved }: { template: ChecklistTemplate; onClose: () => void; onSaved: () => Promise<void> }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [name, setName] = useState(template.name);
  const [items, setItems] = useState<EditItem[]>(() => template.items.map((i) => ({ ...i, powerTypes: [...i.powerTypes], key: `k${++keySeq}` })));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const issues = useMemo(() => validateChecklistTemplate(name, items), [name, items]);
  const show = touched ? issues : [];
  const itemIssues = (n: number) => show.filter((x: TemplateIssue) => x.index === n).map((x) => t(`templateEdit.issue.${x.code}`));
  const topIssues = show.filter((x) => x.index === null).map((x) => t(`templateEdit.issue.${x.code}`));

  const update = (key: string, p: Partial<EditItem>) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const move = (n: number, d: -1 | 1) =>
    setItems((xs) => {
      const m = n + d;
      if (m < 0 || m >= xs.length) return xs;
      const next = [...xs];
      [next[n], next[m]] = [next[m]!, next[n]!];
      return next;
    });
  const add = () =>
    setItems((xs) => [...xs, { key: `k${++keySeq}`, code: "", textTr: "", textEn: "", critical: false, photoRequiredOnFail: false, powerTypes: [], impactFirst: false, allowNa: false }]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (issues.length > 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await provider.saveChecklistTemplate({ id: template.id, name, items: items.map(({ key: _k, ...rest }) => rest) });
      await onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t("templateEdit.title")}
      onClose={() => !busy && onClose()}
      onSubmit={(e) => void submit(e)}
      wide
      dismissible={!busy}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <FormField id="tpl-name" label={t("templateEdit.name")} error={topIssues.find((x) => x === t("templateEdit.issue.name_required") || x === t("templateEdit.issue.name_too_long")) ?? null}>
        <input value={name} maxLength={CHECKLIST_LIMITS.templateNameMax + 10} onChange={(e) => setName(e.target.value)} {...fieldAria("tpl-name", {})} />
      </FormField>
      <Note>{t("templateEdit.noLock")}</Note>
      <ol className="cl-edit-items">
        {items.map((i, n) => {
          const errs = itemIssues(n);
          const base = `tpl-${i.key}`;
          return (
            <li key={i.key} className={`cl-edit-item${errs.length ? " has-issue" : ""}`} aria-label={t("templateEdit.itemLabel", { n: n + 1 })}>
              <div className="cl-edit-row">
                <div className="field cl-edit-code">
                  <label htmlFor={`${base}-code`}>{t("templateEdit.code")}</label>
                  <input id={`${base}-code`} value={i.code} maxLength={8} onChange={(e) => update(i.key, { code: e.target.value.toUpperCase() })} />
                </div>
                <div className="field cl-edit-text">
                  <label htmlFor={`${base}-tr`}>{t("templateEdit.textTr")}</label>
                  <input id={`${base}-tr`} value={i.textTr} onChange={(e) => update(i.key, { textTr: e.target.value })} />
                </div>
                <div className="cl-edit-move">
                  <button type="button" className="icon-button cl-up" onClick={() => move(n, -1)} disabled={n === 0} title={t("templateEdit.moveUp")} aria-label={t("templateEdit.moveUp")}>
                    <Icon name="chevronDown" size={16} />
                  </button>
                  <button type="button" className="icon-button" onClick={() => move(n, 1)} disabled={n === items.length - 1} title={t("templateEdit.moveDown")} aria-label={t("templateEdit.moveDown")}>
                    <Icon name="chevronDown" size={16} />
                  </button>
                  <button type="button" className="icon-button" onClick={() => setItems((xs) => xs.filter((x) => x.key !== i.key))} title={t("templateEdit.remove")} aria-label={t("templateEdit.remove")}>
                    <Icon name="trash" size={16} />
                  </button>
                </div>
              </div>
              <div className="field">
                <label htmlFor={`${base}-en`}>
                  {t("templateEdit.textEn")}
                  <span className="field-optional"> · {t("form.optional")}</span>
                </label>
                <input id={`${base}-en`} value={i.textEn} onChange={(e) => update(i.key, { textEn: e.target.value })} />
              </div>
              <div className="cl-edit-flags">
                <label className="check-row">
                  <input type="checkbox" checked={i.critical} onChange={(e) => update(i.key, { critical: e.target.checked })} />
                  <span>{t("templateEdit.critical")}</span>
                </label>
                <label className="check-row">
                  <input type="checkbox" checked={i.photoRequiredOnFail} onChange={(e) => update(i.key, { photoRequiredOnFail: e.target.checked })} />
                  <span>{t("templateEdit.photo")}</span>
                </label>
                <label className="check-row">
                  <input type="checkbox" checked={i.impactFirst} onChange={(e) => update(i.key, { impactFirst: e.target.checked })} />
                  <span>{t("templateEdit.impactFirst")}</span>
                </label>
                <label className="check-row">
                  <input type="checkbox" checked={i.allowNa} disabled={!i.critical} onChange={(e) => update(i.key, { allowNa: e.target.checked })} />
                  <span>{t("templateEdit.allowNa")}</span>
                </label>
                <label className="check-row">
                  <input type="checkbox" checked={i.powerTypes.length === 1 && i.powerTypes[0] === "lpg"} onChange={(e) => update(i.key, { powerTypes: e.target.checked ? ["lpg"] : [] })} />
                  <span>{t("templateEdit.lpgOnly")}</span>
                </label>
              </div>
              {errs.length > 0 && <p className="field-error">{[...new Set(errs)].join(" ")}</p>}
            </li>
          );
        })}
      </ol>
      {topIssues.filter((x) => x !== t("templateEdit.issue.name_required") && x !== t("templateEdit.issue.name_too_long")).map((x) => (
        <p key={x} className="field-error">
          {x}
        </p>
      ))}
      <button type="button" className="button button-secondary button-sm" onClick={add} disabled={items.length >= CHECKLIST_LIMITS.itemsMax}>
        <Icon name="plus" size={15} />
        {t("templateEdit.add")}
      </button>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}
